import { useState, useEffect, useMemo } from "react";
import { MapPin, ChevronDown, ChevronUp, X } from "lucide-react";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";
import type { LeafletMouseEvent } from "leaflet";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BaseMap } from "./BaseMap";
import { fetchGeoJSONFromUrl } from "@/utils/geojsonLoader";

const WORLD_GEOJSON_URL = "/geojson/regions/WORLD.json";

interface CountryFilterProps {
  /**
   * List of available countries that have content
   * (countries not in this list will be disabled/grayed out)
   */
  availableCountries: string[];

  /**
   * Currently selected country (null means no filter)
   */
  selectedCountry: string | null;

  /**
   * Callback when country selection changes
   */
  onCountryChange: (country: string | null) => void;

  /**
   * Optional callback when a country is selected (not deselected)
   * Useful for triggering actions like scrolling to results
   */
  onCountrySelected?: (country: string) => void;
}

/**
 * CountryFilter - Interactive map-based country filter component
 *
 * Features:
 * - Collapsible to save space when not in use
 * - Shows world map with clickable countries
 * - Highlights countries with available content
 * - Disables countries without content
 * - Immediate filtering on selection
 */
export function CountryFilter({
  availableCountries,
  selectedCountry,
  onCountryChange,
  onCountrySelected,
}: CountryFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [worldData, setWorldData] = useState<FeatureCollection<Geometry, GeoJsonProperties> | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  // Load world GeoJSON when component mounts or when opened
  useEffect(() => {
    if (!isOpen || worldData) return;

    fetchGeoJSONFromUrl(WORLD_GEOJSON_URL)
      .then((data) => setWorldData(data))
      .catch((error) => {
        console.error("Failed to load world map:", error);
      });
  }, [isOpen, worldData]);

  // Convert available countries to a Set for O(1) lookup
  const availableCountriesSet = useMemo(
    () => new Set(availableCountries),
    [availableCountries]
  );

  // Style features based on availability and selection
  const getFeatureStyle = (feature: Feature<Geometry, GeoJsonProperties>) => {
    const countryName = feature.properties?.name as string | undefined;
    if (!countryName) {
      return {
        fillColor: "#e5e7eb",
        fillOpacity: 0.3,
        color: "#d1d5db",
        weight: 1,
      };
    }

    const isAvailable = availableCountriesSet.has(countryName);
    const isSelected = countryName === selectedCountry;
    const isHovered = countryName === hoveredCountry;

    if (!isAvailable) {
      // Disabled countries (no content)
      return {
        fillColor: "#f3f4f6",
        fillOpacity: 0.2,
        color: "#e5e7eb",
        weight: 0.5,
        className: "opacity-30",
      };
    }

    if (isSelected) {
      // Selected country
      return {
        fillColor: "#3b82f6", // blue-500
        fillOpacity: 0.6,
        color: "#1e40af", // blue-800
        weight: 2,
      };
    }

    if (isHovered) {
      // Hovered available country
      return {
        fillColor: "#60a5fa", // blue-400
        fillOpacity: 0.5,
        color: "#2563eb", // blue-600
        weight: 2,
      };
    }

    // Available but not selected/hovered
    return {
      fillColor: "#93c5fd", // blue-300
      fillOpacity: 0.3,
      color: "#3b82f6", // blue-500
      weight: 1,
    };
  };

  // Handle country click
  const handleFeatureClick = (
    feature: Feature<Geometry, GeoJsonProperties>,
    _event: LeafletMouseEvent
  ) => {
    const countryName = feature.properties?.name as string | undefined;
    if (!countryName || !availableCountriesSet.has(countryName)) {
      return; // Ignore clicks on disabled countries
    }

    // If clicking the same country, deselect it
    if (countryName === selectedCountry) {
      onCountryChange(null);
    } else {
      onCountryChange(countryName);
      // Notify parent that a country was selected (not deselected)
      onCountrySelected?.(countryName);
    }
  };

  // Handle country hover
  const handleFeatureMouseOver = (
    feature: Feature<Geometry, GeoJsonProperties>,
    _event: LeafletMouseEvent
  ) => {
    const countryName = feature.properties?.name as string | undefined;
    if (countryName && availableCountriesSet.has(countryName)) {
      setHoveredCountry(countryName);
    }
  };

  const handleFeatureMouseOut = () => {
    setHoveredCountry(null);
  };

  const clearSelection = () => {
    onCountryChange(null);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-foreground/50">
          Country
        </p>
        <CollapsibleTrigger asChild>
          <button className="flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-foreground/40" />
              {selectedCountry ? (
                <span className="font-medium text-foreground">{selectedCountry}</span>
              ) : (
                <span className="text-foreground/60">Select on map</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedCountry && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelection();
                  }}
                  className="rounded-full p-0.5 hover:bg-foreground/10"
                  aria-label="Clear country filter"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-foreground/40" />
              ) : (
                <ChevronDown className="h-4 w-4 text-foreground/40" />
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {worldData ? (
            <BaseMap
              data={worldData}
              mapHeight={450}
              disableAutoFitBounds={true}
              initialZoom={2}
              getFeatureStyle={getFeatureStyle}
              onFeatureClick={handleFeatureClick}
              onFeatureMouseOver={handleFeatureMouseOver}
              onFeatureMouseOut={handleFeatureMouseOut}
              showTooltip
              renderTooltipContent={(feature) => {
                const countryName = feature.properties?.name as string | undefined;
                if (!countryName) return null;

                const isAvailable = availableCountriesSet.has(countryName);
                if (!isAvailable) {
                  return (
                    <div className="text-foreground/60">
                      <div className="font-semibold">{countryName}</div>
                      <div className="text-xs">No content available</div>
                    </div>
                  );
                }

                return (
                  <div>
                    <div className="font-semibold">{countryName}</div>
                    <div className="text-xs text-foreground/60">
                      Click to filter
                    </div>
                  </div>
                );
              }}
            />
          ) : (
            <div className="flex h-[450px] items-center justify-center rounded-3xl border border-border/50 bg-background/50 text-sm text-foreground/60">
              Loading world map...
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
